import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  Plus,
  MapPin,
  DollarSign,
  ShoppingCart,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Store as StoreIcon,
  AlertCircle,
  Monitor,
  Map,
  CheckCircle,
  Navigation,
  ZoomIn,
  ZoomOut,
  X,
  Upload,
} from 'lucide-angular';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StoreService } from '../../core/services/store.service';
import { StoreTypesService } from '../../core/services/store-types.service';
import {
  StoreSummary,
  CreateStoreRequest,
  UpdateStoreRequest,
  StoreTypeDto,
} from '../../core/models/store.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';
import { ProductService } from '../../core/services/product.service';
import {
  ImportPreviewFn,
  ImportSubmitFn,
  ProductImportDialogComponent,
} from '../../shared/components/product-import/product-import-dialog.component';

interface LLatLng {
  lat: number;
  lng: number;
}
interface LBounds {
  getSouth(): number;
  getNorth(): number;
  getWest(): number;
  getEast(): number;
  getCenter(): LLatLng;
}
interface LLayer {
  getBounds(): LBounds;
}
interface LFeatureGroup {
  clearLayers(): void;
  addLayer(layer: LLayer): void;
  eachLayer(fn: (l: LLayer) => void): void;
}
interface LMap {
  setView(c: [number, number], z: number): LMap;
  addLayer(l: LFeatureGroup): void;
  addControl(c: unknown): void;
  on(event: string, handler: (e: unknown) => void): void;
  remove(): void;
  flyTo(ll: LLatLng, z: number, opts?: unknown): void;
}
interface LDrawCreatedEvent {
  layer: LLayer;
}
interface LDrawEditedEvent {
  layers: LFeatureGroup;
}
interface LCircleMarker {
  bindPopup(t: string): { openPopup(): void };
}
interface LeafletStatic {
  map(el: HTMLElement, opts?: unknown): LMap;
  tileLayer(url: string, opts?: unknown): { addTo(m: LMap): void };
  FeatureGroup: new () => LFeatureGroup;
  latLng(lat: number, lng: number): LLatLng;
  latLngBounds(sw: [number, number], ne: [number, number]): LBounds;
  rectangle(b: LBounds, opts?: unknown): LLayer;
  circleMarker(ll: LLatLng, opts?: unknown): { addTo(m: LMap): LCircleMarker };
  Draw: { Event: { CREATED: string; EDITED: string; DELETED: string } };
  Control: { Draw: new (opts: unknown) => unknown };
}
declare const L: LeafletStatic;

@Component({
  selector: 'app-stores',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    LucideAngularModule,
    FormsModule,
    TranslatePipe,
    ProductImportDialogComponent,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './stores.component.html',
})
export class StoresComponent implements OnInit {
  private readonly storeService = inject(StoreService);
  private readonly confirm = inject(ConfirmationService);
  private readonly storeTypesService = inject(StoreTypesService);
  private readonly productService = inject(ProductService);
  /** Import oynasidagi valyuta tanlagichi uchun — shablondan o'qiladi. */
  readonly currencySvc = inject(CurrencyService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef$ = inject(DestroyRef);

  Plus = Plus;
  MapPin = MapPin;
  DollarSign = DollarSign;
  ShoppingCart = ShoppingCart;
  MoreVertical = MoreVertical;
  Eye = Eye;
  Edit = Edit;
  Trash2 = Trash2;
  StoreIcon = StoreIcon;
  AlertCircle = AlertCircle;
  Monitor = Monitor;
  Map = Map;
  CheckCircle = CheckCircle;
  Navigation = Navigation;
  ZoomIn = ZoomIn;
  ZoomOut = ZoomOut;
  X = X;
  Upload = Upload;

  // ── Mahsulot import ────────────────────────────────────────────────────────
  //
  // Oyna har do'kon uchun O'SHA do'konga qulflab ochiladi — kartochkadan kirilgani
  // uchun do'kon allaqachon ma'lum va uni qayta tanlatish xato manbai bo'lardi.
  importDialogOpen = signal(false);
  importStoreId = signal<string | null>(null);
  importStoreName = signal('');

  /**
   * Oynaga so'rov funksiyalari sifatida uzatiladi. Maydon (arrow) sifatida
   * saqlanadi, chunki shablonda har tekshiruvda yangi funksiya yaratilsa
   * `input` qiymati o'zgargan hisoblanib, oyna cheksiz qayta chizilardi.
   */
  readonly importPreviewFn: ImportPreviewFn = (file) => this.productService.previewImport(file);
  readonly importSubmitFn: ImportSubmitFn = (file, mapping, storeId, currencies) =>
    this.productService.importProducts(file, mapping, storeId, currencies);

  storeList = signal<StoreSummary[]>([]);
  storeTypes = signal<StoreTypeDto[]>([]);
  isLoading = signal(false);
  isDialogOpen = signal(false);
  isEditMode = signal(false);
  errorMessage = signal<string | null>(null);

  // Bbox state
  bboxDialogOpen = signal(false);
  bboxStoreId = signal('');
  bboxStoreName = signal('');
  bboxSaving = signal(false);
  bboxResult: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null = null;
  bboxAreaM2 = signal<number | null>(null);
  locating = signal(false);

  private leafletMap: LMap | null = null;
  private drawnItems: LFeatureGroup | null = null;
  private currentLayer: LLayer | null = null;

  formData = { id: '', name: '', address: '', isActive: true, storeTypeId: null as string | null };

  ngOnInit() {
    this.loadStores();
    this.storeTypesService
      .getAll(true)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((res) => this.storeTypes.set(res.data ?? []));
  }

  openImportDialog(store: StoreSummary) {
    this.importStoreId.set(store.storeId);
    this.importStoreName.set(store.storeName);
    this.importDialogOpen.set(true);
  }

  /** Import tugagach kartochkalardagi qoldiq/tushum raqamlari eskirgan bo'ladi. */
  onProductsImported() {
    this.loadStores();
  }

  loadStores() {
    this.isLoading.set(true);
    this.storeService
      .getStoreSummary()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          if (res.success) this.storeList.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set('Failed to load stores.');
          this.isLoading.set(false);
        },
      });
  }

  openDialog(store?: StoreSummary) {
    if (store) {
      this.isEditMode.set(true);
      this.formData = {
        id: store.storeId,
        name: store.storeName,
        address: store.address,
        isActive: store.isActive,
        storeTypeId: store.storeTypeId,
      };
    } else {
      this.isEditMode.set(false);
      this.formData = { id: '', name: '', address: '', isActive: true, storeTypeId: null };
    }
    this.isDialogOpen.set(true);
  }

  closeDialog() {
    this.isDialogOpen.set(false);
    this.errorMessage.set(null);
  }

  handleSubmit() {
    if (!this.formData.name || !this.formData.address) return;
    // StoreType majburiy — kategoriya/birlik/variant shu turga bog'lanadi.
    if (!this.formData.storeTypeId) {
      this.errorMessage.set(this.translate.instant('STORES.FORM.STORE_TYPE_REQUIRED'));
      return;
    }
    this.isLoading.set(true);
    if (this.isEditMode()) {
      const req: UpdateStoreRequest = {
        id: this.formData.id,
        name: this.formData.name,
        address: this.formData.address,
        isActive: this.formData.isActive,
        storeTypeId: this.formData.storeTypeId,
      };
      this.storeService
        .updateStore(this.formData.id, req)
        .pipe(takeUntilDestroyed(this.destroyRef$))
        .subscribe({
          next: () => {
            this.loadStores();
            this.closeDialog();
          },
          error: (err) => {
            this.errorMessage.set(err.error?.message || 'Update failed');
            this.isLoading.set(false);
          },
        });
    } else {
      const req: CreateStoreRequest = {
        name: this.formData.name,
        address: this.formData.address,
        storeTypeId: this.formData.storeTypeId,
      };
      this.storeService
        .createStore(req)
        .pipe(takeUntilDestroyed(this.destroyRef$))
        .subscribe({
          next: () => {
            this.loadStores();
            this.closeDialog();
          },
          error: (err) => {
            this.errorMessage.set(err.error?.message || 'Creation failed');
            this.isLoading.set(false);
          },
        });
    }
  }

  navigateToPos(storeId: string) {
    this.router.navigate(['/app/pos', storeId]);
  }

  viewAnalytics(storeId: string) {
    this.router.navigate(['/app/analytics'], { queryParams: { storeId } });
  }

  /**
   * Backend buyurtma tarixi yoki qoldig'i bor do'konni o'chirishga yo'l qo'ymaydi
   * (`DeleteStoreCommand`), shuning uchun xato matni foydalanuvchiga ko'rsatiladi —
   * uni yutib yuborsak, kassir nima uchun o'chmaganini bilmay qolardi.
   */
  handleDelete(store: StoreSummary) {
    this.confirm.confirm({
      message: this.translate.instant('STORES.DELETE_CONFIRM', { name: store.storeName }),
      header: this.translate.instant('COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.storeService
          .deleteStore(store.storeId)
          .pipe(takeUntilDestroyed(this.destroyRef$))
          .subscribe({
            next: () => {
              this.storeService.showDeleteSuccess();
              this.loadStores();
            },
            error: (err) => this.storeService.showDeleteError(err),
          }),
    });
  }

  // в”Ђв”Ђв”Ђ Bbox Dialog в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ

  openBboxDialog(store: StoreSummary) {
    this.bboxStoreId.set(store.storeId);
    this.bboxStoreName.set(store.storeName);
    this.bboxResult = null;
    this.bboxAreaM2.set(null);
    this.bboxDialogOpen.set(true);
    setTimeout(() => this.initBboxMap(), 150);
  }

  closeBboxDialog() {
    this.bboxDialogOpen.set(false);
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
      this.drawnItems = null;
      this.currentLayer = null;
    }
  }

  private initBboxMap() {
    const container = document.getElementById('bbox-map');
    if (!container || typeof L === 'undefined') return;

    // Default: Toshkent
    this.leafletMap = L.map(container, { zoomControl: true }).setView([41.2995, 69.2401], 17);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: 'В© OpenStreetMap',
      maxZoom: 22,
    }).addTo(this.leafletMap);

    // Drawn items layer
    this.drawnItems = new L.FeatureGroup();
    this.leafletMap.addLayer(this.drawnItems);

    // Leaflet.Draw controls — faqat to'rtburchak
    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        rectangle: {
          shapeOptions: { color: '#4f46e5', weight: 2, fillOpacity: 0.2 },
          showArea: true,
          metric: true,
        },
        polygon: false,
        polyline: false,
        circle: false,
        circlemarker: false,
        marker: false,
      },
      edit: {
        featureGroup: this.drawnItems,
        edit: { selectedPathOptions: { color: '#4f46e5' } },
        remove: true,
      },
    });
    this.leafletMap.addControl(drawControl);

    // Rectangle chizilganda
    this.leafletMap.on(L.Draw.Event.CREATED, (e: unknown) => {
      const ev = e as LDrawCreatedEvent;
      this.drawnItems!.clearLayers();
      this.drawnItems!.addLayer(ev.layer);
      this.currentLayer = ev.layer;
      this.updateBboxResult(ev.layer.getBounds());
    });

    // Rectangle tahrirlangandan keyin
    this.leafletMap.on(L.Draw.Event.EDITED, (e: unknown) => {
      (e as LDrawEditedEvent).layers.eachLayer((layer: LLayer) => {
        this.currentLayer = layer;
        this.updateBboxResult(layer.getBounds());
      });
    });

    // Rectangle o'chirilganda
    this.leafletMap.on(L.Draw.Event.DELETED, () => {
      this.bboxResult = null;
      this.bboxAreaM2.set(null);
      this.currentLayer = null;
    });

    // GPS bilan joylashuvni topib markazga o'rnatamiz
    this.flyToMyLocation(false);
  }

  flyToMyLocation(showMarker = true) {
    if (!navigator.geolocation) return;
    this.locating.set(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating.set(false);
        const latlng = L.latLng(pos.coords.latitude, pos.coords.longitude);
        this.leafletMap?.flyTo(latlng, 18, { animate: true, duration: 1 });
        if (showMarker && this.leafletMap) {
          L.circleMarker(latlng, {
            radius: 10,
            color: '#4f46e5',
            fillColor: '#818cf8',
            fillOpacity: 0.8,
            weight: 2,
          })
            .addTo(this.leafletMap)
            .bindPopup('Siz shu yerdasiz')
            .openPopup();
        }
      },
      () => this.locating.set(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  expandBbox(meters: number) {
    if (!this.currentLayer || !this.drawnItems) return;
    const bounds = this.currentLayer.getBounds();
    const latDelta = meters / 111320;
    const lonDelta = meters / (111320 * Math.cos((bounds.getCenter().lat * Math.PI) / 180));
    const newBounds = L.latLngBounds(
      [bounds.getSouth() - latDelta, bounds.getWest() - lonDelta],
      [bounds.getNorth() + latDelta, bounds.getEast() + lonDelta],
    );
    this.drawnItems.clearLayers();
    const rect = L.rectangle(newBounds, { color: '#4f46e5', weight: 2, fillOpacity: 0.2 });
    this.drawnItems.addLayer(rect);
    this.currentLayer = rect;
    this.updateBboxResult(newBounds);
  }

  shrinkBbox(meters: number) {
    if (!this.currentLayer || !this.drawnItems) return;
    const bounds = this.currentLayer.getBounds();
    const latDelta = meters / 111320;
    const lonDelta = meters / (111320 * Math.cos((bounds.getCenter().lat * Math.PI) / 180));

    const newSouth = bounds.getSouth() + latDelta;
    const newNorth = bounds.getNorth() - latDelta;
    const newWest = bounds.getWest() + lonDelta;
    const newEast = bounds.getEast() - lonDelta;

    if (newSouth >= newNorth || newWest >= newEast) return;

    const newBounds = L.latLngBounds([newSouth, newWest], [newNorth, newEast]);
    this.drawnItems.clearLayers();
    const rect = L.rectangle(newBounds, { color: '#4f46e5', weight: 2, fillOpacity: 0.2 });
    this.drawnItems.addLayer(rect);
    this.currentLayer = rect;
    this.updateBboxResult(newBounds);
  }

  private updateBboxResult(bounds: LBounds) {
    this.bboxResult = {
      minLat: bounds.getSouth(),
      maxLat: bounds.getNorth(),
      minLon: bounds.getWest(),
      maxLon: bounds.getEast(),
    };
    // Calculate approximate area in mВІ
    const latM = (bounds.getNorth() - bounds.getSouth()) * 111320;
    const lonM =
      (bounds.getEast() - bounds.getWest()) *
      111320 *
      Math.cos((bounds.getCenter().lat * Math.PI) / 180);
    this.bboxAreaM2.set(Math.round(latM * lonM));
  }

  saveBbox() {
    if (!this.bboxResult) {
      this.storeService.showWarn(this.translate.instant('STORES.MAP.DRAW_FIRST'));
      return;
    }
    this.bboxSaving.set(true);
    this.storeService
      .updateBbox(this.bboxStoreId(), {
        bboxMinLat: this.bboxResult.minLat,
        bboxMaxLat: this.bboxResult.maxLat,
        bboxMinLon: this.bboxResult.minLon,
        bboxMaxLon: this.bboxResult.maxLon,
      })
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () => {
          this.bboxSaving.set(false);
          this.closeBboxDialog();
          this.loadStores();
        },
        error: (err) => {
          this.storeService.showUpdateError(err);
          this.bboxSaving.set(false);
        },
      });
  }

  clearBbox(storeId: string) {
    this.confirm.confirm({
      message: this.translate.instant('STORES.MAP.CLEAR_CONFIRM'),
      header: this.translate.instant('COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () =>
        this.storeService
          .updateBbox(storeId, {
            bboxMinLat: null,
            bboxMaxLat: null,
            bboxMinLon: null,
            bboxMaxLon: null,
          })
          .pipe(takeUntilDestroyed(this.destroyRef$))
          .subscribe({
            next: () => {
              this.storeService.showDeleteSuccess();
              this.loadStores();
            },
          }),
    });
  }

  formatArea(m2: number | null): string {
    if (m2 === null) return '';
    if (m2 >= 10000) return `${(m2 / 10000).toFixed(2)} ga`;
    return `${m2.toLocaleString()} mВІ`;
  }
}
